import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Save, Info, Eye, Target, MapPin, Quote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBilingual = () => ({ ar: '', en: '' });

const emptyState = () => ({
  header: { title: emptyBilingual(), subtitle: emptyBilingual() },
  body: {
    kicker: emptyBilingual(),
    brand_name: emptyBilingual(),
    intro: emptyBilingual(),
    paragraph2: emptyBilingual(),
  },
  vision: { title: emptyBilingual(), body: emptyBilingual() },
  mission: { title: emptyBilingual(), body: emptyBilingual() },
  closing: { text: emptyBilingual() },
  contact: {
    address: emptyBilingual(),
    website_url: '',
    website_display: '',
  },
});

// Bilingual (AR + EN) side-by-side field
const BilingualField = ({ label, value, onChange, testIdBase, textarea = false, rows = 5 }) => {
  const Comp = textarea ? Textarea : Input;
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-slate-700">{label}</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Arabic (AR)</Label>
          <Comp
            dir="rtl"
            value={value?.ar || ''}
            onChange={(e) => onChange({ ...(value || {}), ar: e.target.value })}
            data-testid={`${testIdBase}-ar`}
            placeholder="النص بالعربية"
            className="text-right"
            {...(textarea ? { rows } : {})}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">English (EN)</Label>
          <Comp
            value={value?.en || ''}
            onChange={(e) => onChange({ ...(value || {}), en: e.target.value })}
            data-testid={`${testIdBase}-en`}
            placeholder="Text in English"
            {...(textarea ? { rows } : {})}
          />
        </div>
      </div>
    </div>
  );
};

export const AdminAboutView = () => {
  const { t } = useLanguage();
  const [state, setState] = useState(emptyState());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/about`);
        const base = emptyState();
        setState({
          header: { ...base.header, ...(r.data?.header || {}) },
          body: { ...base.body, ...(r.data?.body || {}) },
          vision: { ...base.vision, ...(r.data?.vision || {}) },
          mission: { ...base.mission, ...(r.data?.mission || {}) },
          closing: { ...base.closing, ...(r.data?.closing || {}) },
          contact: { ...base.contact, ...(r.data?.contact || {}) },
        });
      } catch {
        toast.error(t('تعذّر تحميل محتوى صفحة من نحن', 'Failed to load About-Us content'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const updateSection = (section, key, val) =>
    setState((s) => ({ ...s, [section]: { ...s[section], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/about`, state, { withCredentials: true });
      toast.success(t('تم حفظ محتوى صفحة من نحن', 'About-Us content saved successfully'));
    } catch (e) {
      toast.error(t('حدث خطأ أثناء الحفظ', 'Failed to save changes'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center text-slate-500 py-12" data-testid="about-cms-loading">
        {t('جاري التحميل...', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-about-cms">
      {/* Header + Save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
            <Info size={22} className="text-[#1565c0]" />
            {t('محتوى صفحة "من نحن"', 'About-Us Page Content')}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t(
              'حرّر النصوص التي تظهر للزوار في صفحة "من نحن" (متاح بالعربية والإنجليزية)',
              'Edit the copy shown to visitors on the About-Us page (Arabic + English)',
            )}
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="about-cms-save-button"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>

      {/* Header band */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-header-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Info size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الترويسة العلوية', 'Header Band')}</h3>
        </div>
        <BilingualField
          label={t('العنوان الرئيسي', 'Page Title')}
          value={state.header.title}
          onChange={(v) => updateSection('header', 'title', v)}
          testIdBase="about-header-title"
        />
        <BilingualField
          label={t('العنوان الفرعي', 'Subtitle')}
          value={state.header.subtitle}
          onChange={(v) => updateSection('header', 'subtitle', v)}
          testIdBase="about-header-subtitle"
        />
      </Card>

      {/* Body */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-body-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Quote size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الفقرات التعريفية', 'Body Paragraphs')}</h3>
        </div>
        <BilingualField
          label={t('نص علوي صغير (Kicker)', 'Small Kicker')}
          value={state.body.kicker}
          onChange={(v) => updateSection('body', 'kicker', v)}
          testIdBase="about-body-kicker"
        />
        <BilingualField
          label={t('اسم العلامة', 'Brand Name')}
          value={state.body.brand_name}
          onChange={(v) => updateSection('body', 'brand_name', v)}
          testIdBase="about-body-brand"
        />
        <BilingualField
          label={t('الفقرة الأولى', 'Paragraph 1 (Intro)')}
          value={state.body.intro}
          onChange={(v) => updateSection('body', 'intro', v)}
          testIdBase="about-body-intro"
          textarea
          rows={6}
        />
        <BilingualField
          label={t('الفقرة الثانية', 'Paragraph 2')}
          value={state.body.paragraph2}
          onChange={(v) => updateSection('body', 'paragraph2', v)}
          testIdBase="about-body-paragraph2"
          textarea
          rows={5}
        />
      </Card>

      {/* Vision */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-vision-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Eye size={18} className="text-[#1565c0]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الرؤية', 'Vision')}</h3>
        </div>
        <BilingualField
          label={t('عنوان الرؤية', 'Vision Title')}
          value={state.vision.title}
          onChange={(v) => updateSection('vision', 'title', v)}
          testIdBase="about-vision-title"
        />
        <BilingualField
          label={t('نص الرؤية', 'Vision Body')}
          value={state.vision.body}
          onChange={(v) => updateSection('vision', 'body', v)}
          testIdBase="about-vision-body"
          textarea
          rows={5}
        />
      </Card>

      {/* Mission */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-mission-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Target size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الرسالة', 'Mission')}</h3>
        </div>
        <BilingualField
          label={t('عنوان الرسالة', 'Mission Title')}
          value={state.mission.title}
          onChange={(v) => updateSection('mission', 'title', v)}
          testIdBase="about-mission-title"
        />
        <BilingualField
          label={t('نص الرسالة', 'Mission Body')}
          value={state.mission.body}
          onChange={(v) => updateSection('mission', 'body', v)}
          testIdBase="about-mission-body"
          textarea
          rows={5}
        />
      </Card>

      {/* Closing */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-closing-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <Quote size={18} className="text-[#1565c0]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('الفقرة الختامية', 'Closing Paragraph')}</h3>
        </div>
        <BilingualField
          label={t('النص الختامي', 'Closing Text')}
          value={state.closing.text}
          onChange={(v) => updateSection('closing', 'text', v)}
          testIdBase="about-closing-text"
          textarea
          rows={4}
        />
      </Card>

      {/* Contact */}
      <Card className="p-6 space-y-5 border border-slate-200" data-testid="about-cms-contact-card">
        <div className="flex items-center gap-2 pb-2 border-b">
          <MapPin size={18} className="text-[#22c8b8]" />
          <h3 className="text-lg font-extrabold text-slate-800">{t('بيانات التواصل', 'Contact Info')}</h3>
        </div>
        <BilingualField
          label={t('العنوان', 'Address')}
          value={state.contact.address}
          onChange={(v) => updateSection('contact', 'address', v)}
          testIdBase="about-contact-address"
          textarea
          rows={2}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">{t('رابط الموقع', 'Website URL')}</Label>
            <Input
              value={state.contact.website_url || ''}
              onChange={(e) => updateSection('contact', 'website_url', e.target.value)}
              placeholder="https://www.tababa.co"
              data-testid="about-contact-website-url"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">
              {t('نص الرابط الظاهر', 'Website Display Text')}
            </Label>
            <Input
              value={state.contact.website_display || ''}
              onChange={(e) => updateSection('contact', 'website_display', e.target.value)}
              placeholder="www.tababa.co"
              data-testid="about-contact-website-display"
            />
          </div>
        </div>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1565c0] hover:bg-[#0d47a1] text-white"
          data-testid="about-cms-save-button-bottom"
        >
          <Save size={16} className="mr-2" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ التغييرات', 'Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default AdminAboutView;
